import Image from 'next/image';
import Cookies from 'js-cookie';

import { Box, Button, Divider, Heading, Modal, Paragraph, TextButton } from '@zillow/constellation-10';

import { CLAIM_YOUR_LISTING_COOKIES } from '../../../constants/CookieConstants';

import RentCheckSVG from '../../../../assets/rent-check.svg';

import {
  emitUseGetStartedFirstModalEvent,
  emitUserXOutFirstModalEvent,
  USE_GET_STARTED_FIRST_MODAL_EVENT_ID,
} from '../../../analytics/events/claimDashboard';
import { useAuth } from '../../AuthProvider';
import ClaimYourListingDetails from '../../ClaimYourListingDetails';

const ClaimPropertiesModal = ({
  onClose,
  isClaimMoreModal,
  newPropertiesCount,
}: {
  onClose: () => void;
  isClaimMoreModal: boolean;
  newPropertiesCount: number;
}) => {
  const { user } = useAuth();

  const handleClose = () => {
    Cookies.set(
      isClaimMoreModal
        ? CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_MORE_MODAL
        : CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_MODAL,
      'true',
      {
        expires: CLAIM_YOUR_LISTING_COOKIES.EXPIRATION,
      },
    );
    onClose();
  };

  return (
    <Modal.Root
      open={true}
      onOpenChange={(open: boolean) => {
        if (!open) {
          handleClose();
          emitUserXOutFirstModalEvent({ user });
        }
      }}
      size={{ base: 'fullScreen', md: 'md' }}
      useDismissProps={{ outsidePress: false }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              {isClaimMoreModal ? (
                <>&nbsp;</>
              ) : (
                <Heading level={2} textStyle="heading-sm" css={{ textAlign: 'center' }}>
                  Claim and list your properties for free
                </Heading>
              )}
            </Modal.Header>
            <Modal.Body css={{ padding: isClaimMoreModal ? '0 !important' : 'default' }}>
              {isClaimMoreModal ? (
                <>
                  <Box css={{ position: 'relative', width: '100%', height: 'auto', aspectRatio: '720/204' }}>
                    <Image
                      src={RentCheckSVG}
                      alt="An apartment with a checkmark"
                      fill
                      style={{ objectFit: 'contain' }}
                    />
                  </Box>
                  <Box css={{ padding: '400' }}>
                    <Heading level={2} textStyle="heading-sm">
                      We found {newPropertiesCount} new {newPropertiesCount === 1 ? 'property' : 'properties'} to claim
                    </Heading>
                    <Paragraph css={{ marginBlock: '400' }}>
                      Review the address{newPropertiesCount > 1 ? 'es' : ''} to make sure{' '}
                      {newPropertiesCount > 1 ? `they’re` : `it’s`} yours, then claim and publish your free listing
                      {newPropertiesCount > 1 ? 's' : ''}. When vacancies arise, you&apos;ll be ready to upgrade and
                      advertise.
                    </Paragraph>
                  </Box>
                </>
              ) : (
                <ClaimYourListingDetails isFromModal={true} />
              )}
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <Box>
                {!isClaimMoreModal && (
                  <TextButton css={{ marginRight: '600' }} onClick={handleClose}>
                    Cancel
                  </TextButton>
                )}
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={() => {
                    handleClose();
                    emitUseGetStartedFirstModalEvent({ user });
                  }}
                  asChild
                  data-eventid={USE_GET_STARTED_FIRST_MODAL_EVENT_ID}
                >
                  <a href="/rental-manager/ad-management/how-claiming-works">
                    {isClaimMoreModal ? 'Review and claim' : 'Get started'}
                  </a>
                </Button>
              </Box>
            </Modal.Footer>
            {isClaimMoreModal && <Modal.CloseButton />}
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default ClaimPropertiesModal;
